/**
 * Every finding in the organisation as `FindingItem`s, with the people who can
 * be assigned and the actions the queue, the detail page and the policy tab
 * share. Reads the regulatory findings and the suggestions once each; gap
 * suggestions become findings, record updates are left to their own page.
 *
 * @module components/regulation/finding/useFindingItems
 */
import type { DismissCategory, FindingPlan } from "@repo/shared/findingPlanning";
import { libraryDocumentName } from "@repo/shared/findingQueue";
import { useCallback, useMemo } from "react";

import {
  planFinding,
  transitionFinding,
  useOrganizationFindings,
  useOrganizationPeople,
  useRecommendations,
  type OrganizationPerson,
} from "../../../state/governance";
import type { OrgProfileUnavailableReason } from "../../../state/orgProfile";
import { usePolicies } from "../../../state/policies";
import { findingItems, type FindingItem } from "./findingItem";
import { personLabel } from "./findingGlyphs";

/** Library names by document id, the same names the library shows. */
export function useDocumentNames(organizationId: string | null): ReadonlyMap<string, string> {
  const { snapshot: library } = usePolicies(organizationId);
  return useMemo(
    () =>
      new Map(
        (library?.policies ?? []).map((policy) => [policy.id, libraryDocumentName(policy)]),
      ),
    [library],
  );
}

export interface FindingActions {
  /** Assign or set a due date. */
  readonly plan: (item: FindingItem, plan: FindingPlan) => Promise<string | null>;
  readonly dismiss: (item: FindingItem, input: { readonly category: DismissCategory; readonly note: string }) => Promise<string | null>;
  /** A regulatory finding resolved by a file version, or by a note on the current one. */
  readonly resolveFinding: (item: FindingItem, input: { readonly resolvedVersion?: number; readonly note: string | null }) => Promise<string | null>;
  readonly reopen: (item: FindingItem) => Promise<string | null>;
}

export interface FindingItemsView {
  readonly items: ReadonlyArray<FindingItem>;
  /** Both sources answered. */
  readonly ready: boolean;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly refresh: () => void;
  readonly people: ReadonlyArray<OrganizationPerson>;
  readonly peopleById: ReadonlyMap<string, OrganizationPerson>;
  readonly personName: (id: string) => string | null;
  readonly documentNames: ReadonlyMap<string, string>;
  readonly actions: FindingActions;
}

export function useFindingItems(organizationId: string | null): FindingItemsView {
  const findings = useOrganizationFindings(organizationId);
  const suggestions = useRecommendations(organizationId);
  const people = useOrganizationPeople(organizationId);
  const documentNames = useDocumentNames(organizationId);
  const refreshFindings = findings.refresh;
  const refreshSuggestions = suggestions.refresh;
  const decide = suggestions.decide;

  const items = useMemo(
    () =>
      findings.snapshot && suggestions.snapshot
        ? findingItems(findings.snapshot.findings, suggestions.snapshot.recommendations, documentNames)
        : [],
    [findings.snapshot, suggestions.snapshot, documentNames],
  );
  const peopleList = useMemo(() => people.snapshot?.people ?? [], [people.snapshot]);
  const peopleById = useMemo(() => new Map(peopleList.map((person) => [person.userId, person])), [peopleList]);
  const personName = useCallback(
    (id: string) => {
      const person = peopleById.get(id);
      return person ? personLabel(person) : null;
    },
    [peopleById],
  );
  const refresh = useCallback(() => {
    refreshFindings();
    refreshSuggestions();
  }, [refreshFindings, refreshSuggestions]);

  const actions = useMemo<FindingActions>(() => {
    const after = (origin: FindingItem["origin"]) => (error: string | null) => {
      // A refusal ("already decided") means this page is behind; refresh either way.
      if (origin === "finding") refreshFindings();
      else refreshSuggestions();
      return error;
    };
    // Suggestions refresh on success inside `decide`; a refusal must refresh too.
    const afterSuggestion = (error: string | null) => (error === null ? error : after("suggestion")(error));
    const missingOrg = async () => "No organization selected.";
    return {
      plan: (item, plan) =>
        organizationId === null
          ? missingOrg()
          : planFinding({
              organizationId,
              target:
                item.origin === "finding" && item.policyId
                  ? { origin: "finding", policyId: item.policyId, id: item.id }
                  : { origin: "suggestion", id: item.id },
              plan,
            }).then(after(item.origin)),
      dismiss: (item, input) =>
        organizationId === null
          ? missingOrg()
          : item.origin === "finding" && item.policyId
            ? transitionFinding({
                organizationId,
                policyId: item.policyId,
                findingId: item.id,
                status: "closed",
                note: input.note,
                dismissCategory: input.category,
              }).then(after("finding"))
            : decide(item.id, "dismiss", { note: input.note, dismissCategory: input.category }).then(afterSuggestion),
      resolveFinding: (item, input) =>
        organizationId === null || item.origin !== "finding" || !item.policyId
          ? missingOrg()
          : transitionFinding({
              organizationId,
              policyId: item.policyId,
              findingId: item.id,
              status: "resolved",
              ...(input.resolvedVersion === undefined ? {} : { resolvedVersion: input.resolvedVersion }),
              note: input.note,
            }).then(after("finding")),
      reopen: (item) =>
        organizationId === null
          ? missingOrg()
          : item.origin === "finding" && item.policyId
            ? transitionFinding({ organizationId, policyId: item.policyId, findingId: item.id, status: "open" }).then(after("finding"))
            : decide(item.id, "reopen").then(afterSuggestion),
    };
  }, [organizationId, decide, refreshFindings, refreshSuggestions]);

  return {
    items,
    ready: findings.snapshot !== null && suggestions.snapshot !== null,
    unavailableReason: findings.unavailableReason ?? suggestions.unavailableReason ?? null,
    refresh,
    people: peopleList,
    peopleById,
    personName,
    documentNames,
    actions,
  };
}
